import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { TreeDeciduous, Plus, Users, Calendar, ArrowLeft, Settings, Download, MoreVertical, Trash2, Edit, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialMemberState = {
  first_name: "",
  last_name: "",
  birth_date: "",
  death_date: "",
  occupation: "",
  location: "",
  bio: "",
  email: "",
  historical_notes: "",
  relationships: "",
  parent_member_id: "",
  mother_member_id: "",
  generation: 0
};

// Generation labels for clarity
const GENERATION_OPTIONS = [
  { value: 0, label: "Generation 0 - Great-grandparents" },
  { value: 1, label: "Generation 1 - Grandparents" },
  { value: 2, label: "Generation 2 - Parents" },
  { value: 3, label: "Generation 3 - You / Siblings" },
  { value: 4, label: "Generation 4 - Children" },
  { value: 5, label: "Generation 5 - Grandchildren" },
  { value: 6, label: "Generation 6 - Great-grandchildren" }
];

// Extracted Member Select Component
function ParentSelect({ members, value, onChange, label, testId }) {
  const options = members.map(function(m) {
    return { id: m.member_id, label: m.first_name + " " + m.last_name };
  });
  
  return (
    <Select value={value || "none"} onValueChange={onChange}>
      <SelectTrigger data-testid={testId || "member-parent-select"}>
        <SelectValue placeholder={label || "Select parent"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {options.map(function(opt) {
          return <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  );
}

// Extracted Privacy Select
function PrivacySelect({ value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="admin_only">Admin Only</SelectItem>
        <SelectItem value="all_members">All Members</SelectItem>
        <SelectItem value="public">Public</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Member Card Component
function MemberCard({ member, onNavigate, onDelete, isAdmin }) {
  return (
    <Card className="bg-card rounded-2xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer hover:border-primary/30" data-testid={"member-card-" + member.member_id}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1" onClick={function() { onNavigate(member.member_id); }}>
            <Avatar className="h-12 w-12">
              <AvatarImage src={member.photo_url} />
              <AvatarFallback className="bg-secondary text-secondary-foreground">
                {(member.first_name || "").charAt(0)}{(member.last_name || "").charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">{member.first_name} {member.last_name}</CardTitle>
              <CardDescription>
                {member.birth_date ? "Born " + member.birth_date : ""}
                {member.death_date ? " - Died " + member.death_date : ""}
              </CardDescription>
            </div>
          </div>
          {isAdmin ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={function() { onNavigate(member.member_id); }}>
                  <Edit className="w-4 h-4 mr-2" /> View/Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { onDelete(member.member_id); }} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0" onClick={function() { onNavigate(member.member_id); }}>
        {member.occupation ? <p className="text-sm text-muted-foreground mb-1">{member.occupation}</p> : null}
        {member.location ? <p className="text-sm text-muted-foreground">{member.location}</p> : null}
      </CardContent>
    </Card>
  );
}

const BranchPage = function({ user }) {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editBranchOpen, setEditBranchOpen] = useState(false);
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const [branchUsers, setBranchUsers] = useState([]);
  const [newMember, setNewMember] = useState(initialMemberState);
  const [editBranch, setEditBranch] = useState({ name: "", description: "", privacy: "" });

  useEffect(function() {
    var fetchBranchData = async function() {
      try {
        var results = await Promise.all([
          fetch(API + "/branches/" + branchId, { credentials: "include" }),
          fetch(API + "/members?branch_id=" + branchId, { credentials: "include" })
        ]);

        if (!results[0].ok) {
          toast.error("Branch not found");
          navigate("/dashboard");
          return;
        }

        var branchData = await results[0].json();
        setBranch(branchData);
        setEditBranch({ 
          name: branchData.name, 
          description: branchData.description || "", 
          privacy: branchData.privacy 
        });

        if (results[1].ok) {
          setMembers(await results[1].json());
        }
      } catch (error) {
        toast.error("Failed to load branch");
      } finally {
        setLoading(false);
      }
    };
    fetchBranchData();
  }, [branchId, navigate]);

  var handleAddMember = async function(e) {
    e.preventDefault();
    try {
      var response = await fetch(API + "/members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...newMember, branch_id: branchId })
      });

      if (!response.ok) throw new Error("Failed");

      var member = await response.json();
      setMembers([...members, member]);
      setAddMemberOpen(false);
      setNewMember(initialMemberState);
      toast.success("Member added!");
    } catch (error) {
      toast.error("Failed to add member");
    }
  };

  var handleUpdateBranch = async function(e) {
    e.preventDefault();
    try {
      var response = await fetch(API + "/branches/" + branchId, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editBranch)
      });

      if (!response.ok) throw new Error("Failed");

      var updatedBranch = await response.json();
      setBranch({ ...branch, ...updatedBranch });
      setEditBranchOpen(false);
      toast.success("Branch updated!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  var handleDeleteBranch = async function() {
    try {
      var response = await fetch(API + "/branches/" + branchId, {
        method: "DELETE",
        credentials: "include"
      });

      if (!response.ok) throw new Error("Failed");
      toast.success("Branch deleted");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  var handleDeleteMember = async function(memberId) {
    try {
      await fetch(API + "/members/" + memberId, { method: "DELETE", credentials: "include" });
      setMembers(members.filter(function(m) { return m.member_id !== memberId; }));
      toast.success("Member removed");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  var handleExport = async function() {
    try {
      var response = await fetch(API + "/export/" + branchId, { credentials: "include" });
      if (!response.ok) throw new Error("Failed");
      var data = await response.json();
      var blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = branch.name.toLowerCase().replace(/\s+/g, '-') + "-family-tree.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  var fetchBranchUsers = async function() {
    try {
      var response = await fetch(API + "/branches/" + branchId + "/users", { credentials: "include" });
      if (response.ok) {
        var users = await response.json();
        setBranchUsers(users);
      }
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  var handleUpdateRole = async function(userId, newRole) {
    try {
      var response = await fetch(API + "/branches/" + branchId + "/users/" + userId + "/role", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        toast.success("Role updated to " + newRole);
        fetchBranchUsers();
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  var navigateToMember = function(memberId) {
    navigate("/member/" + memberId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!branch) return null;

  var membersByGen = {};
  members.forEach(function(m) {
    var gen = m.generation || 0;
    if (!membersByGen[gen]) membersByGen[gen] = [];
    membersByGen[gen].push(m);
  });

  var generations = Object.keys(membersByGen).sort(function(a, b) { return Number(a) - Number(b); });

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate("/dashboard"); }} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-8 h-8 text-primary" />
              <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
            </div>
          </div>
          
          {branch.is_admin ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={function() { navigate("/branch/" + branchId + "/invite"); }} data-testid="invite-btn" className="rounded-full">
                <UserPlus className="w-4 h-4 mr-2" /> Invite
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="branch-menu-btn">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={function() { setManageUsersOpen(true); }}>
                    <Users className="w-4 h-4 mr-2" /> Manage Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={function() { setEditBranchOpen(true); }}>
                    <Settings className="w-4 h-4 mr-2" /> Edit Branch
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExport}>
                    <Download className="w-4 h-4 mr-2" /> Export Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={function() { navigate("/branch/" + branchId + "/timeline"); }}>
                    <Calendar className="w-4 h-4 mr-2" /> Timeline
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-10">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">{branch.name}</h1>
              <p className="text-lg text-muted-foreground">{branch.description || "No description"}</p>
            </div>
            {branch.is_admin ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-destructive" data-testid="delete-branch-btn">
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Branch?</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently delete the branch and all members.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteBranch} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
          <div className="flex items-center gap-6 mt-4">
            <span className="text-sm text-muted-foreground">
              <Users className="w-4 h-4 inline mr-1" />{members.length} members
            </span>
            <span className="text-sm text-muted-foreground capitalize">Privacy: {(branch.privacy || "").replace("_", " ")}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {branch.is_admin ? (
            <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground rounded-full" data-testid="add-member-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Member
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add Family Member</DialogTitle>
                <DialogDescription>Add a new member to your family branch.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddMember} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input data-testid="member-first-name" value={newMember.first_name} onChange={function(e) { setNewMember({ ...newMember, first_name: e.target.value }); }} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input data-testid="member-last-name" value={newMember.last_name} onChange={function(e) { setNewMember({ ...newMember, last_name: e.target.value }); }} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Birth Date</Label>
                    <Input type="date" data-testid="member-birth-date" value={newMember.birth_date} onChange={function(e) { setNewMember({ ...newMember, birth_date: e.target.value }); }} />
                  </div>
                  <div className="space-y-2">
                    <Label>Death Date</Label>
                    <Input type="date" data-testid="member-death-date" value={newMember.death_date} onChange={function(e) { setNewMember({ ...newMember, death_date: e.target.value }); }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Occupation</Label>
                  <Input data-testid="member-occupation" value={newMember.occupation} onChange={function(e) { setNewMember({ ...newMember, occupation: e.target.value }); }} />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input data-testid="member-location" value={newMember.location} onChange={function(e) { setNewMember({ ...newMember, location: e.target.value }); }} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" data-testid="member-email" value={newMember.email} onChange={function(e) { setNewMember({ ...newMember, email: e.target.value }); }} />
                </div>
                <div className="space-y-2">
                  <Label>Biography</Label>
                  <Textarea data-testid="member-bio" value={newMember.bio} onChange={function(e) { setNewMember({ ...newMember, bio: e.target.value }); }} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Historical Notes</Label>
                  <Textarea data-testid="member-historical-notes" value={newMember.historical_notes} onChange={function(e) { setNewMember({ ...newMember, historical_notes: e.target.value }); }} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Relationships / Marriages</Label>
                  <Textarea 
                    data-testid="member-relationships" 
                    value={newMember.relationships} 
                    onChange={function(e) { setNewMember({ ...newMember, relationships: e.target.value }); }} 
                    rows={2}
                    placeholder="e.g., Married to Jane (1960-1975), Married to Mary (1976-1990)"
                  />
                  <p className="text-xs text-muted-foreground">List spouses, marriages, and dates</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Father</Label>
                    <ParentSelect 
                      members={members} 
                      value={newMember.parent_member_id} 
                      onChange={function(v) { setNewMember({ ...newMember, parent_member_id: v === "none" ? "" : v }); }}
                      label="Select father"
                      testId="member-father-select"
                    />
                    <p className="text-xs text-muted-foreground">Select from existing members</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Mother</Label>
                    <ParentSelect 
                      members={members} 
                      value={newMember.mother_member_id} 
                      onChange={function(v) { setNewMember({ ...newMember, mother_member_id: v === "none" ? "" : v }); }}
                      label="Select mother"
                      testId="member-mother-select"
                    />
                    <p className="text-xs text-muted-foreground">Select from existing members</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Generation</Label>
                  <Select 
                    value={String(newMember.generation)} 
                    onValueChange={function(v) { setNewMember({ ...newMember, generation: parseInt(v) }); }}
                  >
                    <SelectTrigger data-testid="member-generation-select">
                      <SelectValue placeholder="Select generation" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENERATION_OPTIONS.map(function(opt) {
                        return <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Select the generation level of this family member</p>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setAddMemberOpen(false); }}>Cancel</Button>
                  <Button type="submit" data-testid="submit-member-btn">Add Member</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          ) : null}

          <Button variant="outline" className="rounded-full" onClick={function() { navigate("/branch/" + branchId + "/timeline"); }} data-testid="view-timeline-btn">
            <Calendar className="w-4 h-4 mr-2" /> Timeline
          </Button>
          <Button variant="outline" className="rounded-full" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {members.length === 0 ? (
          <Card className="bg-card rounded-2xl border border-border/60 p-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-xl font-medium text-foreground mb-2">No family members yet</h3>
            <p className="text-muted-foreground mb-6">
              {branch.is_admin 
                ? "Start building your family tree by adding the first member"
                : "No family members have been added to this branch yet"
              }
            </p>
            {branch.is_admin && (
              <Button onClick={function() { setAddMemberOpen(true); }} className="bg-primary text-primary-foreground rounded-full" data-testid="empty-add-member-btn">
                <Plus className="w-4 h-4 mr-2" /> Add First Member
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-8">
            {generations.map(function(gen) {
              return (
                <div key={gen}>
                  <h3 className="font-display text-lg font-medium text-muted-foreground mb-4">Generation {gen}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {membersByGen[gen].map(function(member) {
                      return <MemberCard key={member.member_id} member={member} onNavigate={navigateToMember} onDelete={handleDeleteMember} isAdmin={branch.is_admin} />;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={editBranchOpen} onOpenChange={setEditBranchOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Branch</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateBranch} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Branch Name</Label>
              <Input data-testid="edit-branch-name" value={editBranch.name} onChange={function(e) { setEditBranch({ ...editBranch, name: e.target.value }); }} required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="edit-branch-description" value={editBranch.description} onChange={function(e) { setEditBranch({ ...editBranch, description: e.target.value }); }} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Privacy</Label>
              <PrivacySelect value={editBranch.privacy} onChange={function(v) { setEditBranch({ ...editBranch, privacy: v }); }} testId="edit-branch-privacy" />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={function() { setEditBranchOpen(false); }}>Cancel</Button>
              <Button type="submit" data-testid="save-branch-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={manageUsersOpen} onOpenChange={function(open) { setManageUsersOpen(open); if (open) fetchBranchUsers(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Manage Users</DialogTitle>
            <DialogDescription>View and update user roles for this branch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-96 overflow-y-auto">
            {branchUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No users have joined this branch yet</p>
            ) : (
              branchUsers.map(function(u) {
                return (
                  <div key={u.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground">
                          {(u.name || u.email || "?").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{u.name || "Unknown"}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <Select value={u.role} onValueChange={function(v) { handleUpdateRole(u.user_id, v); }}>
                      <SelectTrigger className="w-28" data-testid={"role-select-" + u.user_id}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={function() { setManageUsersOpen(false); }}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchPage;
